/*
 * 版权所有 (c) 华为技术有限公司 2024
 * Copyright (c) Huawei Technologies Co., Ltd. 2024. All rights reserved.
 */
import { type AfterViewInit, type OnDestroy, type OnInit, Component, Injector, Input, ViewChild } from '@angular/core';
import { FileUpload } from '@dm/ang';
import { type Polling, ActionAbstract, CookieService, HttpService, LazyloadService, PollingService, UNPLACEHOLDER } from '@dm/core';
import { ERROR_OK } from 'app/common/constants';
import { AuthService } from 'app/common/services';
import { PatchService } from 'app/common/services/patch.service';
import { SWITCH } from 'app/config';

import { type UpgradeDetail, CheckFileProcesses, Progress, UPD_STATUS_E, UPGRADE_QUERY_TYPE, UPLOAD_STATUS } from '../upgrade.const';
import { UpgradeService } from '../upgrade.service';
import { UpgradeComponent } from '../upgrade-panel/upgrade.component';
import { UpgradeStrService } from '../upgrade-str.service';
import { UploadFailDetailComponent } from '../upload-fail-detail/upload-fail-detail.component';

@Component({
    selector: 'app-upgrade-upload',
    templateUrl: './upgrade-upload.component.html',
    styleUrls: ['./upgrade-upload.component.scss'],
})

export class UpgradeUploadComponent extends ActionAbstract implements OnInit, OnDestroy, AfterViewInit {
    // 上传窗口显示
    @Input() show: boolean;
    // 升级面板组件
    @Input() upgrade: UpgradeComponent;
    /**
     * 当前版本
     */
    @Input() currentVersion: string;
    @ViewChild('uploadEl') uploadEl: FileUpload;
    isJa = this.i18nService.language === 'ja';

    UNPLACEHOLDER = UNPLACEHOLDER;
    UPLOAD_STATUS = UPLOAD_STATUS;
    SWITCH = SWITCH;

    // 目标热补丁版本
    targetVersion = UNPLACEHOLDER;
    // 上传包错误信息
    // errorMsg: string;
    // 上传包路径
    uploadUrl: string;
    // 上传进度轮询
    uploadPolling: Polling;
    // 上传进度
    uploadProgress: number;
    // 上传进度条颜色
    progressColor: string;
    // 上传进度条显示
    uploadProgressShow: boolean;
    // 上传状态显示
    uploadstatus: UPLOAD_STATUS;
    // 是否开始热补丁升级流程
    isloadstatus = false;
    /**
     * 上传失败详情
     */
    uploadFailDetails: UpgradeDetail[] = [];

    /**
     * 分发阶段失败
     */
    distributeFail = false;

    uploadMsg = '';
    upgradeBtnDisabled = true;
    i18n = this.str.i18n;

    onSelectPatch = this.patchService.onSelectPatch.bind(this);

    updateCheckFile = true;
    /**
     * 检查项文件上传路径
     */
    checkFileUrl: string;
    /**
     * 当前进度
     */
    currentProcess: Progress;

    // 当时是否正在进行升级
    get isUpgrading(): boolean {
        if (!this.upgrade) {
            return false;
        }
        return Boolean(this.upgrade.checkResultPolling) || Boolean(this.upgrade.upgradeProgressPolling);
    }

    constructor(
        injector: Injector,
        private str: UpgradeStrService,
        private cookieService: CookieService,
        private http: HttpService,
        private poll: PollingService,
        private lazyLoad: LazyloadService,
        private patchService: PatchService,
        private authService: AuthService,
        private service: UpgradeService,
    ) {
        super(injector);
    }
    ngOnInit(): void {
        this.show = true;
        this.uploadUrl = this.http.makeUrl('upgrade.upload').url;
        this.checkFileUrl = this.http.makeUrl('upgrade.upgradeCheckUpload').url;
        this.progressColor = '';
    }

    ngAfterViewInit(): void {
        this.uploadEl.placeholder = this.i18n.hotpatchUploadTip;
    }

    selectCheckFile(): void {
        this.currentProcess = Progress.checkFileSelected;
    }

    startUploadCheckFile(): void {
        this.currentProcess = Progress.checkFileUploading;
        if (!this.authService.getTimeoutExcept()) {
            this.authService.enableTimeoutExcept();
        }
    }

    checkFileUploadCompelete(): void {
        this.currentProcess = Progress.checkFileUploadSuc;
    }

    checkFileUploadFailed(): void {
        this.currentProcess = Progress.checkFileUploadFailed;
    }

    checkFileUploadDisabled(currentProcess: Progress, uploadstatus: UPLOAD_STATUS): boolean {
        const inCheckFileProgress = CheckFileProcesses.includes(currentProcess);
        const hotpatchUploadDone = [
            UPLOAD_STATUS.err,
            UPLOAD_STATUS.suc,
        ].includes(uploadstatus);
        return Boolean(currentProcess && (!inCheckFileProgress || !hotpatchUploadDone));
    }

    checkHotpatchUploadDisabled(currentProcess: Progress, updateCheckFile: boolean): boolean {
        return Boolean(currentProcess !== Progress.checkFileUploadSuc && updateCheckFile);
    }

    // 获取上传包信息
    getPackageInfo(): void {
        const params = {
            CMO_PACKAGE_TYPE: '3',
        };
        this.http.get(this.http.makeUrl('upgrade.packageInfo').setParams(params)).subscribe((result) => {
            this.targetVersion = result.data.CMO_PACKAGE_SPC_VERSION;
            this.upgradeBtnDisabled = false;
        });
    }

    onSelect(): void {
        this.upgradeBtnDisabled = true;
        this.uploadProgress = 0;
        this.progressColor = '';
        this.targetVersion = UNPLACEHOLDER;
        this.uploadstatus = UPLOAD_STATUS.ing;
        this.uploadProgressShow = false;
        this.distributeFail = false;
        if (this.uploadPolling) {
            this.poll.remove(this.uploadPolling);
        }
        if (this.uploadEl.msgs.length > 0) {
            const msg = this.uploadEl.msgs[0];
            this.uploadstatus = UPLOAD_STATUS.err;
            this.uploadMsg = msg.summary;
            if (msg.detail) {
                this.uploadMsg += msg.detail;
            }
            return;
        } else {
            this.uploadMsg = null;
        }
    }

    // 向服务端发送数据前回调函数，常用于自定义header
    onBeforeSend(e): void {
        if (!this.authService.getTimeoutExcept()) {
            // 上传热包,开启超时检测例外
            this.authService.enableTimeoutExcept();
        }
        e.xhr.setRequestHeader('Accept-Language', this.str.getLanguage());
        e.xhr.setRequestHeader('iBaseToken', this.cookieService.getToken());
        e.formData.append('iBaseToken', this.cookieService.getToken());
        e.formData.append('Content-type', 'multipart/form-data');
        this.uploadProgressShow = true;
        this.isloadstatus = true;
        this.uploadProgress = 0;
    }

    // 文件上传前回调函数，由上传按钮点击触发，常用于自定义POST方法的参数
    onBeforeUpload(e): void {
        e.formData = {
            iBaseToken: this.cookieService.getToken(),
            'Content-type': 'multipart/form-data',
        };
    }

    // 上传回传200
    onUpload(e): void {
        // 上传报错
        const error = e.xhr.response && JSON.parse(e.xhr.response).error;
        if (error && Number(error.code) !== ERROR_OK) {
            this.showError(error.description);
            return;
        }

        // 查询分发包进度，0号字段(CMO_EXE_UPD_MSGTYPE)下发数字0
        const rest = this.http.makeUrl('upgrade.uploadProgress').setParams({ CMO_QUERY_TYPE: UPGRADE_QUERY_TYPE.Upload });
        this.uploadPolling = this.poll.add(rest, 1);
        this.uploadPolling.subscribe(({ data }) => {
            this.uploadProgress = Number(data.CMO_UPD_SYS_STEP_STEP_PERCENT);

            // 查询上传进度，状态为6时，表示上传补丁包失败
            if (data.CMO_UPD_SYS_STEP_TASK_STATUS === UPD_STATUS_E.UPD_DLD_FAIL) {
                this.doUploadFailed();
                return;
            }

            if (this.uploadProgress === 100) {
                this.uploadstatus = UPLOAD_STATUS.suc;
                this.getPackageInfo();
                this.poll.remove(this.uploadPolling);
            }
        });
    }

    doUploadFailed(): void {
        this.poll.remove(this.uploadPolling);
        this.service.getDistributeDetail().subscribe((data: UpgradeDetail[]) => {
            if (!data) {
                return;
            }
            this.showError('');
            this.uploadFailDetails = data;
            this.distributeFail = true;
        });
    }

    showError(errorMsg: string): void {
        this.uploadMsg = errorMsg;
        this.onError();
    }

    /**
     * 打开详情失败详情侧滑框
     */
    showUploadFailDetail(): void {
        this.doAction(UploadFailDetailComponent, {
            data: this.uploadFailDetails,
        });
    }

    // 上传失败
    onError(): void {
        this.progressColor = 'red';
        this.uploadstatus = UPLOAD_STATUS.err;
        if (!this.uploadMsg) {
            this.uploadMsg = this.i18nService.get('common.upload_fail_label', [this.i18n.hotpatchPackage]);
        }
    }

    onUpgrade(): void {
        this.upgrade.visible = false;
        this.upgrade.precheck();
        this.show = false;
    }

    onHide(): void {
        this.lazyLoad.destroySelf(this);
    }

    ngOnDestroy(): void {
        // 关闭超时检测例外
        this.authService.disableTimeoutExcept();
        if (this.uploadPolling) {
            this.poll.remove(this.uploadPolling);
        }

        // super.ngOnDestroy()?
    }
}
